import { useEffect, useState } from "react";
import {
  Avatar,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
  LinearProgress,
  Box,
  Fade,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../utilities/Axios";
import { AttachmentTagEnum, CountryCodeDisplayEnum, PassportStatusDisplayEnum, ProcessEnumDisplayLabel, SkillLevelEnum, SkillLevelDisplayEnum } from "../Types/Enum";
import { useSnackbar } from "notistack";
import { Cancel, CheckCircle, OpenInNew, ErrorOutline, HourglassEmpty } from "@mui/icons-material";
import { useTheme } from "../Hooks/ThemeContext/ThemeContext";
import Scaffold from "../Components/Layout/Scaffold";
import { Can } from "../utilities/util";

const API_STEPS = [
  { key: "resume", label: "Resume Data" },
  { key: "jobs", label: "Job List" },
  { key: "experience", label: "Experience List" },
  { key: "countryProcess", label: "Country Process List" },
  { key: "attachments", label: "Attachments" },
  { key: "districts", label: "Districts" },
  { key: "skills", label: "Skills" },
];

type ApiStatusType = "pending" | "loading" | "done" | "error";

const ResumeView = () => {
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  useTheme();

  // Data states
  const [resume, setResume] = useState<any>(null);
  const [jobList, setJobList] = useState<any[]>([]);
  const [experienceList, setExperienceList] = useState<any[]>([]);
  const [resumeCountryProcessList, setResumeCountryProcessList] = useState<any[]>([]);
  const [jobSkillList, setJobSkillList] = useState<any>({});
  const [jobTitleExtra, setJobTitleExtra] = useState<any[]>([]);
  const [jobTitleExtraTitle, setJobTitleExtraTitle] = useState("");
  const [jobTitleExtraChecked, setJobTitleExtraChecked] = useState<any[]>([]);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [districts, setDistricts] = useState<any[]>([]);
  const [resumeSkills, setResumeSkills] = useState<any[]>([]);

  // Timeline status
  const [apiStatus, setApiStatus] = useState<Record<string, ApiStatusType>>({
    resume: "pending",
    jobs: "pending",
    experience: "pending",
    countryProcess: "pending",
    attachments: "pending",
    districts: "pending",
    skills: "pending",
  });

  // Helper to update status
  const updateStatus = (key: string, status: ApiStatusType) => setApiStatus(prev => ({ ...prev, [key]: status }));

  // Timeline icons
  const getStatusIcon = (status: ApiStatusType) => {
    if (status === "done") return <CheckCircle color="success" />;
    if (status === "error") return <ErrorOutline color="error" />;
    if (status === "loading") return <HourglassEmpty color="primary" />;
    return <HourglassEmpty color="disabled" />;
  };

  // Fetch all data with timeline
  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line
  }, []);

  const fetchAllData = async () => {
    // Resume
    try {
      updateStatus("resume", "loading");
      const resumeResponse = await axiosInstance.get(`/v1/resume/${id}`);
      setResume(resumeResponse.data.result);
      updateStatus("resume", "done");

      if (resumeResponse.data.result) {
        const data = resumeResponse.data.result;

        // Jobs
        try {
          updateStatus("jobs", "loading");
          const jobsRes = await axiosInstance.get(`/v1/resume-jobs?resume_id=${data.id}`);
          setJobList(jobsRes.data.result);
          updateStatus("jobs", "done");
        } catch {
          updateStatus("jobs", "error");
        }

        // Experience
        try {
          updateStatus("experience", "loading");
          const expRes = await axiosInstance.get(`/v1/work-experience?resume_id=${data.id}`);
          setExperienceList(expRes.data.result);
          updateStatus("experience", "done");
        } catch {
          updateStatus("experience", "error");
        }

        // Country Process
        try {
          updateStatus("countryProcess", "loading");
          const cpRes = await axiosInstance.get(`/v1/resume-country?resume_id=${data.id}`);
          setResumeCountryProcessList(cpRes.data.result);
          updateStatus("countryProcess", "done");

          const registeredJob = cpRes.data.result.find((item: any) => item.status === 'proc');
          if (registeredJob) {
            getJobExtraList(registeredJob.country_process?.job_title_id, data);
          }
        } catch {
          updateStatus("countryProcess", "error");
        }

        // Attachments
        try {
          updateStatus("attachments", "loading");
          const tags = ['profile_pic', 'full_pic', 'other', 'interview_video'];
          const requests = tags.map(tag =>
            axiosInstance.get(`/v1/resume-attachments?resume_id=${data.id}&attachment_tag=${tag}`)
          );
          const responses = await Promise.all(requests);
          const dataAtt = responses.flatMap(response => response.data.result);
          setAttachments(dataAtt);
          updateStatus("attachments", "done");
        } catch {
          updateStatus("attachments", "error");
        }
      }
    } catch (err: any) {
      updateStatus("resume", "error");
      enqueueSnackbar(`Failed to load resume: ${err.response?.data?.error || err.message}`, {
        variant: "error",
      });
    }

    // Districts
    try {
      updateStatus("districts", "loading");
      const response = await axiosInstance.get('/v1/resume/districts');
      setDistricts(response.data);
      updateStatus("districts", "done");
    } catch {
      updateStatus("districts", "error");
    }

    // Skills
    try {
      updateStatus("skills", "loading");
      const response = await axiosInstance.get(`/v1/resume-skills?resume_id=${id}`);
      setResumeSkills(response.data.result);
      updateStatus("skills", "done");
    } catch {
      updateStatus("skills", "error");
    }
  };

  const getJobExtraList = async (job_title_id: string, _resume: any) => {
    try {
      const response = await axiosInstance.get(`/v1/job-titles/extra/${job_title_id}`);
      const data = response.data;
      setJobTitleExtra(data.result.extra || []);
      setJobTitleExtraTitle(data.result.extra_title);
      setJobTitleExtraChecked(JSON.parse(_resume.job_title_extra || "[]"));
    } catch (err) {
      // silent fail
    }
  };

  // Skill list by job IDs
  useEffect(() => {
    if (resumeCountryProcessList.length > 0) {
      getSkillListsByJobIds(resumeCountryProcessList.map((countryProcess: any) => countryProcess.country_process.job_title_id));
    }
    // eslint-disable-next-line
  }, [resumeCountryProcessList]);

  const getSkillListsByJobIds = async (jobIds: any[]) => {
    try {
      let jobList: { [key: string]: any } = {};
      for (const jobId of jobIds) {
        const response = await axiosInstance.get(
          `/v1/job-title-skills?job_title_id=${jobId}`
        );
        const data = response.data;
        jobList[jobId] = data.result;
      }
      setJobSkillList(jobList);
    } catch (err) {
      // silent fail
    }
  };

  const gotoProcessViewPageChangeHandler = (processViewId: any) => {
    navigate({
      pathname: "/admin/process-view",
      search: `?processId=${processViewId}`,
    });
  };

  // Timeline complete check
  const allDone = Object.values(apiStatus).every(status => status === "done");
  const anyError = Object.values(apiStatus).some(status => status === "error");

  // Timeline UI
  const renderTimeline = () => (
    <Fade in={!allDone} timeout={400}>
      <Box
        sx={{
          position: "fixed",
          inset: 0,
          zIndex: 1300,
          background: "rgba(255,255,255,0.90)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            background: "#fff",
            borderRadius: 4,
            boxShadow: 6,
            p: 4,
            minWidth: 320,
            maxWidth: 400,
            width: "90vw",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, letterSpacing: 1 }}>
            Loading Resume Data
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              width: "100%",
              alignItems: "center",
            }}
          >
            {API_STEPS.map((step, idx) => {
              const status = apiStatus[step.key];
              return (
                <Box
                  key={step.key}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    position: "relative",
                  }}
                >
                  {/* Connector line */}
                  {idx > 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        left: 22,
                        top: -28,
                        width: 3,
                        height: 28,
                        bgcolor: "#e0e0e0",
                        zIndex: 0,
                        borderRadius: 2,
                      }}
                    />
                  )}
                  {/* Step icon */}
                  <Box
                    sx={{
                      zIndex: 1,
                      mr: 2,
                      width: 44,
                      height: 44,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "#f5f5f5",
                      borderRadius: "50%",
                      boxShadow: status === "error" ? 3 : 1,
                    }}
                  >
                    {status === "done" ? (
                      <CheckCircle color="success" fontSize="large" />
                    ) : status === "error" ? (
                      <ErrorOutline color="error" fontSize="large" />
                    ) : (
                      <CircularProgress
                        size={32}
                        thickness={4}
                        color="primary"
                        sx={{ animationDuration: "900ms" }}
                      />
                    )}
                  </Box>
                  {/* Step label */}
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 500,
                      color:
                        status === "error"
                          ? "error.main"
                          : status === "done"
                            ? "success.main"
                            : "text.primary",
                    }}
                  >
                    {step.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
          {anyError && (
            <Typography color="error" sx={{ mt: 2 }}>
              Some data failed to load. Please refresh or check your connection.
            </Typography>
          )}
        </Box>
      </Box>
    </Fade>
  );

  // Show timeline until allDone
  if (!allDone) {
    return (
      <>
        {renderTimeline()}
      </>
    );
  }

  return (
    <Scaffold
      title={`Resume #${resume.customer_reference}`}
      showBackButton={true}
      actions={[]}
      mainAction={null}
    >
      <div>
        {/* --- Personal Information Section --- */}
        <Card className="mb-6">
          <CardHeader title="Personal Information" titleTypographyProps={{ variant: "h5" }} />
          <Divider />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Typography variant="subtitle2" color="textSecondary">Name</Typography>
                <Typography variant="body1">{resume.first_name} {resume.last_name}</Typography>
              </div>
              <div>
                <Typography variant="subtitle2" color="textSecondary">Other Name</Typography>
                <Typography variant="body1">{resume.other_name || "-"}</Typography>
              </div>
              <div>
                <Typography variant="subtitle2" color="textSecondary">NIC</Typography>
                <Typography variant="body1">{resume.nic}</Typography>
              </div>
              <div>
                <Typography variant="subtitle2" color="textSecondary">Date of Birth</Typography>
                <Typography variant="body1">{new Date(resume.birth_of_date).toLocaleDateString()}</Typography>
              </div>
              <div>
                <Typography variant="subtitle2" color="textSecondary">Place of Birth</Typography>
                <Typography variant="body1">{resume.place_of_birth}</Typography>
              </div>
              <div>
                <Typography variant="subtitle2" color="textSecondary">Gender</Typography>
                <Typography variant="body1">{resume.gender === "M" ? "Male" : "Female"}</Typography>
              </div>
              <div>
                <Typography variant="subtitle2" color="textSecondary">Marital Status</Typography>
                <Typography variant="body1" className="capitalize">{resume.marital_status}</Typography>
              </div>
              <div>
                <Typography variant="subtitle2" color="textSecondary">Number of Children</Typography>
                <Typography variant="body1">{resume.number_of_children}</Typography>
              </div>
              <div>
                <Typography variant="subtitle2" color="textSecondary">Religion</Typography>
                <Typography variant="body1" className="capitalize">{resume.religion}</Typography>
              </div>
              <div>
                <Typography variant="subtitle2" color="textSecondary">Qualification</Typography>
                <Typography variant="body1">{resume.qua}</Typography>
              </div>
              <div>
                <Typography variant="subtitle2" color="textSecondary">Complexion</Typography>
                <Typography variant="body1" className="capitalize">{resume.look}</Typography>
              </div>
              <div>
                <Typography variant="subtitle2" color="textSecondary">Height</Typography>
                <Typography variant="body1">
                  {resume.height_unit === 'inch'
                    ? `${resume.foot || 0}' ${resume.height}"`
                    : `${resume.height} cm`}
                </Typography>
              </div>
              <div>
                <Typography variant="subtitle2" color="textSecondary">Weight</Typography>
                <Typography variant="body1">{resume.weight} kg</Typography>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* --- Contact Details Section --- */}
        <Card className="mb-6">
          <CardHeader title="Contact Details" titleTypographyProps={{ variant: "h5" }} />
          <Divider />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Typography variant="subtitle2" color="textSecondary">Mobile Number</Typography>
                <Typography variant="body1">{resume.mobile_no1}</Typography>
              </div>
              <div>
                <Typography variant="subtitle2" color="textSecondary">Whatsapp Mobile</Typography>
                <Typography variant="body1">{resume.mobile_no2 || "-"}</Typography>
              </div>
              <div>
                <Typography variant="subtitle2" color="textSecondary">Home Number</Typography>
                <Typography variant="body1">{resume.home_no1 || "-"}</Typography>
              </div>
              <div>
                <Typography variant="subtitle2" color="textSecondary">Email</Typography>
                <Typography variant="body1">{resume.email_address || "-"}</Typography>
              </div>
              <div className="md:col-span-2 lg:col-span-3">
                <Typography variant="subtitle2" color="textSecondary">Address</Typography>
                <Typography variant="body1">{resume.address}</Typography>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* --- Passport Details Section --- */}
        <Card className="mb-6">
          <CardHeader title="Passport Details" titleTypographyProps={{ variant: "h5" }} />
          <Divider />
          <CardContent>
            {/* Passport Status and Flags */}
            <div className="mb-4 flex flex-wrap gap-2">
              {resume.passport_status && (
                <Chip
                  label={PassportStatusDisplayEnum[resume.passport_status]?.label || resume.passport_status}
                  color={PassportStatusDisplayEnum[resume.passport_status]?.color || "default"}
                  size="small"
                />
              )}
              {resume.old_passport && (
                <Chip
                  label="New Passport"
                  color="info"
                  size="small"
                  variant="outlined"
                />
              )}
              {resume.awaiting_passport && (
                <Chip
                  label="Awaiting Passport"
                  color="warning"
                  size="small"
                  variant="outlined"
                />
              )}
            </div>

            {resume.no_passport ? (
              <Typography variant="body1" color="textSecondary">
                Candidate has no passport.
              </Typography>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <Typography variant="subtitle2" color="textSecondary">Passport Number</Typography>
                    <Typography variant="body1">{resume.passport_no}</Typography>
                  </div>
                  <div>
                    <Typography variant="subtitle2" color="textSecondary">Place of Issue</Typography>
                    <Typography variant="body1">{resume.passport_place_of_issue}</Typography>
                  </div>
                  <div>
                    <Typography variant="subtitle2" color="textSecondary">Date of Issue</Typography>
                    <Typography variant="body1">{new Date(resume.passport_date_of_issue).toLocaleDateString()}</Typography>
                  </div>
                  <div>
                    <Typography variant="subtitle2" color="textSecondary">Expire Date</Typography>
                    <Typography variant="body1">{new Date(resume.passport_expire_date).toLocaleDateString()}</Typography>
                  </div>
                </div>

                {/* Old Passport Details */}
                {resume.old_passport && resume.passport_no_old && (
                  <>
                    <Divider className="my-4" />
                    <Typography variant="h6" className="mb-3">Old Passport Details</Typography>
                    {resume.passport_status_old && (
                      <div className="mb-3">
                        <Chip
                          label={PassportStatusDisplayEnum[resume.passport_status_old]?.label || resume.passport_status_old}
                          color={PassportStatusDisplayEnum[resume.passport_status_old]?.color || "default"}
                          size="small"
                        />
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div>
                        <Typography variant="subtitle2" color="textSecondary">Old Passport Number</Typography>
                        <Typography variant="body1">{resume.passport_no_old}</Typography>
                      </div>
                      <div>
                        <Typography variant="subtitle2" color="textSecondary">Old Place of Issue</Typography>
                        <Typography variant="body1">{resume.passport_place_of_issue_old || "-"}</Typography>
                      </div>
                      <div>
                        <Typography variant="subtitle2" color="textSecondary">Old Date of Issue</Typography>
                        <Typography variant="body1">
                          {resume.passport_date_of_issue_old 
                            ? new Date(resume.passport_date_of_issue_old).toLocaleDateString() 
                            : "-"}
                        </Typography>
                      </div>
                      <div>
                        <Typography variant="subtitle2" color="textSecondary">Old Expire Date</Typography>
                        <Typography variant="body1">
                          {resume.passport_expire_date_old 
                            ? new Date(resume.passport_expire_date_old).toLocaleDateString() 
                            : "-"}
                        </Typography>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* --- Children Details Section --- */}
        {resume.number_of_children > 0 && resume.children && (
          <Card className="mb-6">
            <CardHeader title="Children Details" titleTypographyProps={{ variant: "h5" }} />
            <Divider />
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {resume.children.map((child: any, index: number) => (
                  <Card key={index} variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle1" className="mb-2">Child {index + 1}</Typography>
                      <div className="space-y-2">
                        <div>
                          <Typography variant="subtitle2" color="textSecondary">Name</Typography>
                          <Typography variant="body1">{child.name}</Typography>
                        </div>
                        <div>
                          <Typography variant="subtitle2" color="textSecondary">Date of Birth</Typography>
                          <Typography variant="body1">{new Date(child.dob).toLocaleDateString()}</Typography>
                        </div>
                        <div>
                          <Typography variant="subtitle2" color="textSecondary">Gender</Typography>
                          <Typography variant="body1">{child.gender}</Typography>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* --- Guardian Details Section --- */}
        <Card className="mb-6">
          <CardHeader title="Guardian Details" titleTypographyProps={{ variant: "h5" }} />
          <Divider />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Typography variant="subtitle2" color="textSecondary">Guardian Name</Typography>
                <Typography variant="body1">{resume.guardian_name}</Typography>
              </div>
              <div>
                <Typography variant="subtitle2" color="textSecondary">Relationship</Typography>
                <Typography variant="body1">{resume.guardian_relationship}</Typography>
              </div>
              <div>
                <Typography variant="subtitle2" color="textSecondary">Date of Birth</Typography>
                <Typography variant="body1">{new Date(resume.guardian_dob).toLocaleDateString()}</Typography>
              </div>
              <div>
                <Typography variant="subtitle2" color="textSecondary">NIC</Typography>
                <Typography variant="body1">{resume.guardian_nic}</Typography>
              </div>
              <div className="md:col-span-2 lg:col-span-3">
                <Typography variant="subtitle2" color="textSecondary">Address</Typography>
                <Typography variant="body1">{resume.guardian_address}</Typography>
              </div>
              <div>
                <Typography variant="subtitle2" color="textSecondary">Contact 1</Typography>
                <Typography variant="body1">{resume.guardian_contact1}</Typography>
              </div>
              <div>
                <Typography variant="subtitle2" color="textSecondary">Contact 2</Typography>
                <Typography variant="body1">{resume.guardian_contact2 || "-"}</Typography>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* --- Gramaniladari Information Section --- */}
        <Card className="mb-6">
          <CardHeader title="Gramaniladari Information" titleTypographyProps={{ variant: "h5" }} />
          <Divider />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Typography variant="subtitle2" color="textSecondary">District</Typography>
                <Typography variant="body1">{resume.district}</Typography>
              </div>
              <div>
                <Typography variant="subtitle2" color="textSecondary">Divisional Secretariat</Typography>
                <Typography variant="body1">{resume.grama_dvs}</Typography>
              </div>
              <div>
                <Typography variant="subtitle2" color="textSecondary">Grama Division</Typography>
                <Typography variant="body1">{resume.grama_division}</Typography>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* --- Other Details Section --- */}
        <Card className="mb-6">
          <CardHeader title="Other Details" titleTypographyProps={{ variant: "h5" }} />
          <Divider />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Typography variant="subtitle2" color="textSecondary">Source of Vacancy</Typography>
                {resume.vacancy_source && resume.vacancy_source != "[]"
                  ? JSON.parse(resume.vacancy_source).map((source: string, index: number) => (
                    <Chip key={index} label={source} variant="outlined" size="small" className="mr-1 mb-1" />
                  ))
                  : <Typography variant="body1">-</Typography>}
              </div>
              <div>
                <Typography variant="subtitle2" color="textSecondary">Sub-Agent</Typography>
                <Typography variant="body1">{resume.subagent?.name || "-"}</Typography>
              </div>
              <div>
                <Typography variant="subtitle2" color="textSecondary">Calling Staff</Typography>
                <Typography variant="body1">{resume.staff?.name || "-"}</Typography>
              </div>
              {Can('recruitments', ["VIEW"]) && (
                <>
                  <div>
                    <Typography variant="subtitle2" color="textSecondary">Agreed Commission</Typography>
                    <Typography variant="body1">{resume.agreed_commission ? `LKR ${resume.agreed_commission}` : "-"}</Typography>
                  </div>
                  <div>
                    <Typography variant="subtitle2" color="textSecondary">Monthly Salary</Typography>
                    <Typography variant="body1">{resume.month_salary ? `${resume.month_salary}${resume.salary_currency ? ` ${resume.salary_currency}` : ''}` : "-"}</Typography>
                  </div>
                  <div>
                    <Typography variant="subtitle2" color="textSecondary">Note</Typography>
                    <Typography variant="body1">{resume.note || "-"}</Typography>
                  </div>
                  <div>
                    <Typography variant="subtitle2" color="textSecondary">Training Needed</Typography>
                    <Typography variant="body1">{resume.training_needed ? "Yes" : "No"}</Typography>
                  </div>
                  {Boolean(resume.training_needed) && (
                    <div>
                      <Typography variant="subtitle2" color="textSecondary">Training Amount</Typography>
                      <Typography variant="body1">{resume.training_amount ? `LKR ${resume.training_amount}` : "-"}</Typography>
                    </div>
                  )}
                  <div>
                    <Typography variant="subtitle2" color="textSecondary">Passport Needed</Typography>
                    <Typography variant="body1">{resume.passport_needed ? "Yes" : "No"}</Typography>
                  </div>
                  {Boolean(resume.passport_needed) && (
                    <div>
                      <Typography variant="subtitle2" color="textSecondary">Passport Amount</Typography>
                      <Typography variant="body1">{resume.passport_amount ? `LKR ${resume.passport_amount}` : "-"}</Typography>
                    </div>
                  )}
                  <div className="md:col-span-2 lg:col-span-3">
                    <Typography variant="subtitle2" color="textSecondary">Remark</Typography>
                    <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>{resume.remark || "-"}</Typography>
                  </div>
                </>
              )}
              <div className="md:col-span-2 lg:col-span-3">
                <Typography variant="subtitle2" color="textSecondary">Reception Remark</Typography>
                <Typography variant="body1" sx={{ whiteSpace: "pre-wrap" }}>{resume.reception_remark}</Typography>
              </div>
            </div>
          </CardContent>
        </Card>
        {Can('recruitments', ["VIEW"]) && (
          <>
            {/* --- Experience Details Section --- */}
            {jobList.length > 0 && (
              <Card className="mb-6">
                <CardHeader title="Experience Details" titleTypographyProps={{ variant: "h5" }} />
                <Divider />
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {jobList.map((job, index) => (
                      <Card key={index} variant="outlined">
                        <CardContent>
                          <Typography variant="h6" className="mb-2">{job.job_title.title}</Typography>
                          {experienceList
                            .filter((exp) => exp.job_title_id === job.job_id)
                            .map((exp, expIndex) => (
                              <div key={expIndex} className="mb-2">
                                <Chip
                                  label={`${CountryCodeDisplayEnum[exp.note]} - ${exp.duration}Y${exp.duration_months ? ` ${exp.duration_months}M` : ""}`}
                                  color="primary"
                                  className="mr-2 mb-2"
                                />
                              </div>
                            ))}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* --- Language Details Section --- */}
            {resume.languages && resume.languages.length > 0 && (
              <Card className="mb-6">
                <CardHeader title="Language Details" titleTypographyProps={{ variant: "h5" }} />
                <Divider />
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {resume.languages.map((lang: any, index: number) => (
                      <Card key={index} variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle1" className="mb-2">{lang.name}</Typography>
                          <div className="flex items-center">
                            <Typography variant="subtitle2" className="mr-2">Level:</Typography>
                            <Chip
                              label={lang.level}
                              size="small"
                              color={
                                lang.level === "Very Good" ? "success" :
                                  lang.level === "Good" ? "primary" : "default"
                              }
                            />
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* --- Selected Job Details Section --- */}
            {resumeCountryProcessList.length > 0 && (
              <Card className="mb-6">
                <CardHeader title="Selected Job Details" titleTypographyProps={{ variant: "h5" }} />
                <Divider />
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {resumeCountryProcessList.map((data, index) => (
                      <Card key={index} variant="outlined">
                        <CardContent>
                          <div className="flex justify-between items-start">
                            <div>
                              <Typography variant="h6" className="mb-1">
                                {data.country_process.title}
                              </Typography>
                              <Typography variant="subtitle1" color="textSecondary" className="mb-2">
                                {data.country_process.country_name}
                              </Typography>
                            </div>
                            <Chip
                              label={ProcessEnumDisplayLabel[data.status]}
                              color={
                                data.status === "proc" ? "success" :
                                  data.status === "rejected" ? "error" : "primary"
                              }
                            />
                          </div>
                          {data.process_id && (
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              startIcon={<OpenInNew />}
                              onClick={() => gotoProcessViewPageChangeHandler(data.process_id)}
                              className="mt-2"
                            >
                              View Process
                            </Button>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* --- Skills Section --- */}
            {resumeSkills.length > 0 && (
              <Card className="mb-6">
                <CardHeader title="Skills" titleTypographyProps={{ variant: "h5" }} />
                <Divider />
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {resumeSkills.map((skill, index) => (
                      <Card key={index} variant="outlined">
                        <CardContent>
                          <Typography variant="h6" className="mb-2">{skill.skill_title}</Typography>
                          <Chip
                            label={SkillLevelDisplayEnum[skill.skill_level] || "Not Good"}
                            size="small"
                            color={
                              skill.skill_level === "very_good" ? "success" :
                                skill.skill_level === "good" ? "primary" : "default"
                            }
                          />
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* --- Extra Skills Section --- */}
            {jobTitleExtra.length > 0 && (
              <Card className="mb-6">
                <CardHeader title="Extra Skills" titleTypographyProps={{ variant: "h5" }} />
                <Divider />
                <CardContent>
                  <Typography variant="subtitle1" className="mb-2">{jobTitleExtraTitle}</Typography>
                  <div className="flex flex-wrap gap-2">
                    {jobTitleExtra.map((extra, index) => (
                      <Chip
                        key={index}
                        label={extra}
                        icon={
                          jobTitleExtraChecked.includes(extra) ? (
                            <CheckCircle color="success" />
                          ) : (
                            <Cancel color="error" />
                          )
                        }
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* --- Attachments Section --- */}
            {attachments.length > 0 && (
              <Card className="mb-6">
                <CardHeader title="Attachments" titleTypographyProps={{ variant: "h5" }} />
                <Divider />
                <CardContent>
                  <List>
                    {attachments.map((attachment, index) => (
                      <ListItem key={index} className="hover:bg-slate-50 rounded-md">
                        <ListItemAvatar>
                          {attachment.attachment_type === 'IMAGE' ? (
                            <Avatar
                              src={`${process.env.REACT_APP_PUBLIC_FILE_PATH}${attachment.attachment_url}`}
                              alt={attachment.note}
                            />
                          ) : (
                            <Avatar>{attachment.attachment_type}</Avatar>
                          )}
                        </ListItemAvatar>
                        <ListItemText
                          primary={AttachmentTagEnum[attachment.attachment_tag]}
                          secondary={attachment.note}
                        />
                        <Button
                          variant="contained"
                          color="secondary"
                          className="mx-2"
                          endIcon={<OpenInNew />}
                          onClick={() => {
                            window.open(`${process.env.REACT_APP_PUBLIC_FILE_PATH}${attachment.attachment_url}`, '_blank');
                          }}
                        >
                          View
                        </Button>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </Scaffold>
  );
};

export default ResumeView;